import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { businessServices, subCategories } from '../../utils/categoryData';
import './AdSet.css';

const DASHBOARD_PRICES = {
  'Top Banner':    { '1 Month': 2000, '3 Months': 5000 },
  'Middle Banner': { '1 Month': 2000, '3 Months': 5000 },
  'Bottom Banner': { '1 Month': 1500, '3 Months': 4000 },
};
const CATEGORY_PRICES = { '1 Month': 1000, '3 Months': 2000 };

const BANNER_SIZES = {
  'Top Banner':    '1000 × 450 pixels',
  'Middle Banner': '600 × 600 pixels',
  'Bottom Banner': '1000 × 450 pixels',
};

const packageCards = [
  { title: 'AdSet Banner (Top)',    color: '#1976d2', btnColor: '#7B1FA2', size: '1000 × 450 px', oneMonth: '₹2000', threeMonths: '₹5000' },
  { title: 'AdSet Banner (Middle)', color: '#00897B', btnColor: '#00897B', size: '600 × 600 px',  oneMonth: '₹2000', threeMonths: '₹5000' },
  { title: 'AdSet Banner (Bottom)', color: '#E64A19', btnColor: '#E64A19', size: '1000 × 450 px', oneMonth: '₹1500', threeMonths: '₹4000' },
  { title: 'Business Category',     color: '#3949AB', btnColor: '#7986CB', size: '1000 × 450 px', oneMonth: '₹1000', threeMonths: '₹2000' },
];

const advantages = [
  { title: 'Budget Control',         desc: 'Set budgets at a granular level for better control over advertising expenses.' },
  { title: 'Ad Creative Testing',    desc: 'Test different ad creatives to identify what resonates best with your audience.' },
  { title: 'Placement Optimization', desc: 'Choose where your ads appear, optimizing placement for better performance.' },
  { title: 'Targeted Audience',      desc: 'Define specific audience segments based on demographics, interests, and behaviors.' },
  { title: 'Scheduling',             desc: 'Schedule adsets to run at specific times when your audience is most active.' },
  { title: 'Performance Monitoring', desc: 'Monitor and analyze the performance of each audience segment for ongoing optimization.' },
];

const benefits = [
  'JustService provides consistent and accurate information about your business.',
  'JustService positively impacts your local search rankings.',
  'We allow you to categorize your business appropriately.',
  'This targeted approach can lead to more qualified leads.',
];

const initialForm = {
  fullName: '', phone: '', email: '', address: '',
  pinCode: '', state: '', city: '', businessName: '', gstNumber: '',
  adPlacement: 'Dashboard',
  bannerPosition: 'Top Banner',
  category: '', subCategory: '',
  duration: '1 Month',
  image: null,
};

const HISTORY_FILTERS = ['All', 'pending', 'approved', 'published', 'expired', 'rejected'];

function loadRazorpayScript() {
  return new Promise(resolve => {
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const s = document.createElement('script');
    s.id = 'razorpay-script';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function AdSet() {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();

  const [showUpload, setShowUpload] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [myAds, setMyAds] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('All');
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [packageExpanded, setPackageExpanded] = useState(true);

  const totalAmount = form.adPlacement === 'Dashboard'
    ? (DASHBOARD_PRICES[form.bannerPosition]?.[form.duration] || 0)
    : (CATEGORY_PRICES[form.duration] || 0);

  const categorySubcats = form.category ? (subCategories[form.category] || []) : [];

  const filteredAds = historyFilter === 'All'
    ? myAds
    : myAds.filter(a => a.status === historyFilter);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'adPlacement') {
      setForm(p => ({ ...p, adPlacement: value, bannerPosition: 'Top Banner', category: '', subCategory: '', duration: '1 Month' }));
    } else if (name === 'category') {
      setForm(p => ({ ...p, category: value, subCategory: '' }));
    } else {
      setForm(p => ({ ...p, [name]: value }));
    }
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(p => ({ ...p, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const openUpload = () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    setShowUpload(true); setSubmitted(false); setError('');
  };

  const closeUpload = () => {
    setShowUpload(false); setForm(initialForm);
    setImagePreview(null); setSubmitted(false); setError('');
  };

  const validate = () => {
    if (!form.fullName.trim()) return 'Full name is required';
    if (!form.phone.trim() || !/^[6-9]\d{9}$/.test(form.phone.trim())) return 'Valid 10-digit mobile number is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Valid email is required';
    if (!form.businessName.trim()) return 'Business name is required';
    if (!form.image) return 'Ad image is required';
    if (form.adPlacement === 'Category' && !form.category) return 'Please select a category';
    if (totalAmount === 0) return 'Invalid ad selection';
    return '';
  };

  const handlePayAndSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true); setError('');
    try {
      const { default: api } = await import('../../utils/api');
      const isTestMode = process.env.REACT_APP_RAZORPAY_KEY_ID === 'rzp_test_4xBBjFkRMcqPFH';

      const submitAd = async (paymentData = {}) => {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => { if (v && k !== 'image') fd.append(k, v); });
        fd.append('amount', totalAmount);
        fd.append('image', form.image);
        if (paymentData.razorpay_order_id) fd.append('razorpay_order_id', paymentData.razorpay_order_id);
        if (paymentData.razorpay_payment_id) fd.append('razorpay_payment_id', paymentData.razorpay_payment_id);
        if (paymentData.razorpay_signature) fd.append('razorpay_signature', paymentData.razorpay_signature);
        await api.post('/api/adsets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSubmitted(true);
        setLoading(false);
      };

      // TEST MODE — skip Razorpay, submit directly
      if (isTestMode) {
        await submitAd();
        return;
      }

      // PRODUCTION — Razorpay payment flow
      const orderRes = await api.post('/api/adsets/create-order', {
        fullName: form.fullName, phone: form.phone, email: form.email,
        businessName: form.businessName, adPlacement: form.adPlacement,
        duration: form.duration, bannerPosition: form.bannerPosition,
        category: form.category, amount: totalAmount,
      });
      const { orderId, amount: orderAmount, currency } = orderRes.data.data;

      const loaded = await loadRazorpayScript();
      if (!loaded) { setError('Razorpay failed to load. Check your internet.'); setLoading(false); return; }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderAmount,
        currency,
        name: 'JustService24',
        description: `AdSet - ${form.bannerPosition || form.category} (${form.duration})`,
        order_id: orderId,
        prefill: { name: form.fullName, email: form.email, contact: form.phone },
        theme: { color: '#16a34a' },
        handler: async (paymentResponse) => {
          try {
            await submitAd(paymentResponse);
          } catch (err) {
            setError(err.response?.data?.message || 'Ad submission failed after payment. Contact support.');
            setLoading(false);
          }
        },
        modal: { ondismiss: () => { setError('Payment cancelled.'); setLoading(false); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit. Try again.');
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    setShowHistory(true); setHistoryLoading(true);
    try {
      const { default: api } = await import('../../utils/api');
      const res = await api.get('/api/adsets/my');
      setMyAds(res.data.data || []);
    } catch { setMyAds([]); } finally { setHistoryLoading(false); }
  };

  if (!isLoggedIn) {
    return (
      <div className="adset-login-wall">
        <div className="adset-login-card">
          <div className="adset-login-icon">📢</div>
          <h2>Login Required</h2>
          <p>Please login to access Advertisement features and promote your business.</p>
          <button className="adset-login-btn" onClick={() => navigate('/login')}>Sign Up / Login</button>
          <button className="adset-back-link" onClick={() => navigate(-1)}>← Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="adset-page">

      <div className="adset-topbar">
        <button className="adset-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <h2>JustService AdSet</h2>
      </div>

      <div className="adset-advertise-banner">
        <div className="adset-advertise-text">
          <div className="adset-advertise-label">ADVERTISE</div>
          <div className="adset-advertise-sub">Your Business</div>
          <p>Reach more customers in your district!</p>
        </div>
        <div className="adset-advertise-logo">📢</div>
      </div>

      <div className="adset-container">

        <div className="adset-section-header">
          <span>Upload Adset</span>
          <button className="adset-toggle-btn" onClick={() => setDetailsExpanded(p => !p)}>
            {detailsExpanded ? '▲ View Less' : '▼ View More'}
          </button>
        </div>

        {detailsExpanded && (
          <div className="adset-details-section">
            <h3 className="adset-section-title">(Advertisement Setup)</h3>
            <p><b>* We are excited to showcase your business to our community.</b></p>
            <h4 className="adset-sub-title">Want to Upload Adset?</h4>
            <p>Post Your Ad in your local Area or any Blocks/Districts/States Digitally to reach more People in Lowest cost.</p>
            <h4 className="adset-sub-title">Key Advantages:</h4>
            {advantages.map((a, i) => (
              <div key={i} className="adset-advantage-item">
                <span className="adset-check">✔</span>
                <span><b>{a.title}: </b>{a.desc}</span>
              </div>
            ))}
            <h4 className="adset-sub-title">More Key Benefits:</h4>
            {benefits.map((b, i) => (
              <div key={i} className="adset-benefit-item"><span>✅</span> {b}</div>
            ))}
            <h4 className="adset-sub-title">Customer Reviews and Feedback</h4>
            <p>Business directories often include a review and rating system. Positive reviews can attract more customers, while negative feedback provides valuable insights for improving your services.</p>
          </div>
        )}

        <div className="adset-section-header" style={{ marginTop: 24 }}>
          <span>Package Details</span>
          <button className="adset-toggle-btn" onClick={() => setPackageExpanded(p => !p)}>
            {packageExpanded ? '▲ View Less' : '▼ View More'}
          </button>
        </div>

        {packageExpanded && (
          <div className="adset-packages">
            {packageCards.map((pkg, i) => (
              <div key={i} className="adset-pkg-card" style={{ borderColor: pkg.color }}>
                <div className="adset-pkg-header">
                  <span style={{ color: pkg.color, fontWeight: 700 }}>{pkg.title}</span>
                  <button className="adset-pkg-preview-btn" style={{ background: pkg.btnColor }}>🖼️ Preview</button>
                </div>
                <hr />
                <div className="adset-pkg-size">Banner Size: {pkg.size}</div>
                <hr />
                <div className="adset-pkg-price-row">
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>One Month:</span>
                  <span>{pkg.oneMonth}</span>
                </div>
                <div className="adset-pkg-price-row">
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>Three Months:</span>
                  <span>{pkg.threeMonths}</span>
                </div>
              </div>
            ))}
            <div className="adset-gst-note">Note: GST will be applicable as per the Government's norms.</div>
          </div>
        )}

        <div className="adset-action-row">
          <div className="adset-action-card">
            <span className="adset-action-icon" style={{ color: '#1976d2' }}>☁️</span>
            <button className="adset-action-btn" style={{ background: '#1976d2' }} onClick={openUpload}>
              Upload New Adset
            </button>
            <p>Personal & Business promotion by uploading the AdSet Banner.</p>
          </div>
          <div className="adset-action-card">
            <span className="adset-action-icon" style={{ color: '#16a34a' }}>🕐</span>
            <button className="adset-action-btn" style={{ background: '#16a34a' }} onClick={loadHistory}>
              View Adset History
            </button>
            <p>View your previous adset submission history.</p>
          </div>
        </div>

      </div>

      {/* ── Upload Modal ── */}
      {showUpload && (
        <div className="adset-overlay" onClick={e => e.target === e.currentTarget && closeUpload()}>
          <div className="adset-upload-modal">
            <div className="adset-modal-header">
              <h3>Upload Adset</h3>
              <button onClick={closeUpload}>✕</button>
            </div>

            {submitted ? (
              <div className="adset-success">
                <div>🎉</div>
                <h3>Ad Submitted Successfully!</h3>
                <p>Payment received. Our team will review your advertisement and activate it within 24 hours.</p>
                <button className="adset-action-btn" style={{ background: '#16a34a', marginTop: 16 }} onClick={closeUpload}>Done ✓</button>
              </div>
            ) : (
              <form onSubmit={handlePayAndSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div className="adset-form-scroll">

                  <div className="adset-form-section-title">Customer Details (For Billing)</div>

                  <div className="adset-field">
                    <label>Full Name *</label>
                    <input name="fullName" value={form.fullName} onChange={handleChange} placeholder="Full Name" />
                  </div>
                  <div className="adset-field">
                    <label>Mobile Number *</label>
                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="10-digit Mobile Number" type="tel" maxLength={10} />
                  </div>
                  <div className="adset-field">
                    <label>Business Email *</label>
                    <input name="email" value={form.email} onChange={handleChange} placeholder="Business Email" type="email" />
                  </div>
                  <div className="adset-field">
                    <label>Address</label>
                    <input name="address" value={form.address} onChange={handleChange} placeholder="Address" />
                  </div>
                  <div className="adset-field">
                    <label>PinCode</label>
                    <input name="pinCode" value={form.pinCode} onChange={handleChange} placeholder="PinCode" type="number" />
                  </div>
                  <div className="adset-field-row">
                    <div className="adset-field">
                      <label>State</label>
                      <input name="state" value={form.state} onChange={handleChange} placeholder="State" />
                    </div>
                    <div className="adset-field">
                      <label>City</label>
                      <input name="city" value={form.city} onChange={handleChange} placeholder="City" />
                    </div>
                  </div>
                  <div className="adset-field">
                    <label>Business / Company Name *</label>
                    <input name="businessName" value={form.businessName} onChange={handleChange} placeholder="Business/Company Name" />
                  </div>
                  <div className="adset-field">
                    <label>GST Number (Optional)</label>
                    <input name="gstNumber" value={form.gstNumber} onChange={handleChange} placeholder="GST Number (Optional)" />
                  </div>

                  <div className="adset-form-section-title">Upload Adset Image *</div>
                  <div className="adset-image-upload"
                    onClick={() => document.getElementById('adsetImg').click()}
                    style={{ borderColor: !form.image ? '#f87171' : '#16a34a' }}>
                    {imagePreview
                      ? <img src={imagePreview} alt="preview" style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
                      : <span style={{ color: '#94a3b8' }}>📷 Tap to upload image (Required)</span>
                    }
                    <input id="adsetImg" type="file" accept="image/*" onChange={handleImage} hidden />
                  </div>
                  {imagePreview && (
                    <button type="button" className="adset-remove-img"
                      onClick={() => { setImagePreview(null); setForm(p => ({ ...p, image: null })); }}>
                      🗑️ Remove Image
                    </button>
                  )}

                  <div className="adset-form-section-title">Select Adset Type</div>
                  <div className="adset-tab-row">
                    {['Dashboard', 'Category'].map(tab => (
                      <button key={tab} type="button"
                        className={`adset-tab-btn ${form.adPlacement === tab ? 'active' : ''}`}
                        onClick={() => handleChange({ target: { name: 'adPlacement', value: tab } })}>
                        {tab}
                      </button>
                    ))}
                  </div>

                  {form.adPlacement === 'Dashboard' && (
                    <div className="adset-field">
                      <label>Selected Banner *</label>
                      <select name="bannerPosition" value={form.bannerPosition} onChange={handleChange}>
                        {Object.keys(DASHBOARD_PRICES).map(b => (
                          <option key={b} value={b}>{b} — {BANNER_SIZES[b]}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {form.adPlacement === 'Category' && (
                    <>
                      <div className="adset-field">
                        <label>Select Category *</label>
                        <select name="category" value={form.category} onChange={handleChange}>
                          <option value="">-- Select Category --</option>
                          {businessServices.map(c => (
                            <option key={c.name} value={c.name}>{c.icon} {c.name}</option>
                          ))}
                        </select>
                      </div>
                      {categorySubcats.length > 0 && (
                        <div className="adset-field">
                          <label>Select Sub Category</label>
                          <select name="subCategory" value={form.subCategory} onChange={handleChange}>
                            <option value="">-- Select Sub Category --</option>
                            {categorySubcats.map(s => (
                              <option key={s.name} value={s.name}>{s.icon} {s.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}

                  <div className="adset-field">
                    <label>Select Duration *</label>
                    <select name="duration" value={form.duration} onChange={handleChange}>
                      <option value="1 Month">1 Month</option>
                      <option value="3 Months">3 Months</option>
                    </select>
                  </div>

                </div>

                <div className="adset-modal-footer">
                  <div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>Total Amount</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>₹ {totalAmount}</div>
                  </div>
                  {error && <div className="adset-error-msg">{error}</div>}
                  <button type="submit" className="adset-action-btn" style={{ background: '#16a34a' }} disabled={loading}>
                    {loading ? '⏳ Processing...' : '💳 Pay & Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── History Modal ── */}
      {showHistory && (
        <div className="adset-overlay" onClick={e => e.target === e.currentTarget && setShowHistory(false)}>
          <div className="adset-upload-modal">
            <div className="adset-modal-header">
              <h3>Adset History</h3>
              <button onClick={() => setShowHistory(false)}>✕</button>
            </div>
            <div className="adset-history-filters">
              {HISTORY_FILTERS.map(f => (
                <button key={f}
                  className={`adset-filter-chip ${historyFilter === f ? 'active' : ''}`}
                  onClick={() => setHistoryFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>⏳ Loading...</div>
            ) : filteredAds.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>📢 No ads found.</div>
            ) : (
              <div className="adset-history-list">
                {filteredAds.map((ad, i) => (
                  <div key={i} className="adset-history-item">
                    <div className="adset-history-img">
                      {ad.image
                        ? <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${ad.image}`} alt="ad" />
                        : <span>🖼️</span>}
                    </div>
                    <div className="adset-history-info">
                      <div><b>Ad Type:</b> {ad.adPlacement} {ad.bannerPosition ? `— ${ad.bannerPosition}` : ''}</div>
                      <div><b>Duration:</b> {ad.duration}</div>
                      <div><b>Amount:</b> ₹{ad.amount}</div>
                      <div><b>Date:</b> {new Date(ad.createdAt).toLocaleDateString('en-IN')}</div>
                    </div>
                    <span className={`adset-status-badge adset-status-${ad.status}`}>{ad.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
